import React, { useEffect, useState } from 'react';
import {
    mainMenuTypes, IMenuItem, PasslyInfiniteScroll, PageLoader, PasslyTableRow, PasslyTableCell, PasslyTableSortLabel,
    Filters, PasslyFilterInput
} from '@app/components';
import { getMenuItemById, infiniteScrollItemsFetchLimit, genericErrorToast } from '@app/helpers';
import { IListPageBarEnabled, OrderType, Order, IActiveFilter, ITableHead, IScheduledJobs, FilterEnum } from '@app/models';
import { useSetPage, useTypedTranslation } from '@app/hooks';
import { useSelector, useDispatch } from 'react-redux';
import { scheduledJobsService, basicService } from '@app/services';
import { Table, TableBody, TableHead, TableRow, TableCell, Link } from '@material-ui/core';
import { resetState, setOrderByDirection, setOrderByProperty } from '@app/store/actions';
import { AxiosError } from 'axios';

interface IJobsTableData {
    jobName: string;
    description: string;
    triggers: number;
    id: string;
    scheduler: string;
    jobGroup: string;
}

enum TableHeadEnum {
    jobName = 'JobName',
    description = 'Description',
    triggers = 'Triggers',
    scheduler = 'ScheduleName'
}

const ScheduledJobsPage = () => {
    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.ScheduledJobs);

    const [jobCount, setJobCount] = useState(0);
    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState('JobName');
    const [menuItem] = useState<IMenuItem | undefined>(item);
    const [rows, setRows] = useState<IJobsTableData[]>([]);

    const [loadingData, setLoadingData] = useState<boolean>(true);
    const filtersEnabled = useSelector((state: any) => state.listPageBar.sortingFiltersEnabled);
    const activeFilters: IActiveFilter[] = useSelector((state: any) => state.listPageBar.activeFilters);

    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;
        dispatch(resetState());
        setOrder(ascDesc);
        setOrderBy(property);
        dispatch(setOrderByDirection(ascDesc));
        dispatch(setOrderByProperty(property));
    };

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: true,
        orderByEnabled: true,
        organizationsEnabled: false,
        actionMenuEnabled: false
    };

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.jobName,
            label: 'JOB_NAME'
        },
        {
            id: TableHeadEnum.description,
            label: 'DESCRIPTION'
        },
        {
            id: TableHeadEnum.triggers,
            label: 'JOB_TRIGGERS'
        },
        {
            id: TableHeadEnum.scheduler,
            label: 'JOB_SCHEDULER_NAME'
        }
    ];

    useSetPage(setListPageBarEnabledOptions, menuItem);

    useEffect(() => {
        setLoadingData(true);
    }, []);

    const handleSuccess = (response: any) => {
        const count = response.data['@odata.count'];

        if (count !== undefined) {
            setJobCount(count);
        }

        const results: IScheduledJobs[] = response.data.value;

        if (results) {
            let newRows = results.map((job: IScheduledJobs) => {
                const data: IJobsTableData = {
                    id: job.JobName,
                    jobName: job.JobName,
                    description: job.Description,
                    triggers: job.Triggers.length,
                    scheduler: job.ScheduleName,
                    jobGroup: job.JobGroup
                };
                return data;
            });

            if (results.length === 0) {
                setRows([]);
            }
            else if (count !== undefined) {
                setRows(newRows);
            }
            else {
                setRows(rows.concat(newRows));
            }
        }

        setLoadingData(false);
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        scheduledJobsService.getScheduledJobs(
            orderBy,
            order,
            infiniteScrollItemsFetchLimit,
            skipCount,
            isReset,
            handleSuccess,
            handleError,
            activeFilters
        );
    };

    const handleError = (response: AxiosError<any>) => {
        setLoadingData(false);

        genericErrorToast(response);
    };

    const viewJob = (row: IJobsTableData) => {
        basicService.redirect(`/system/jobs/details/${row.id}?group=${row.jobGroup}&scheduler=${row.scheduler}`);
    };

    return (
        <>
            {filtersEnabled && (
                <Filters>
                    <PasslyFilterInput
                        label={'JOB_NAME'}
                        filtername={FilterEnum.contains}
                        filterby={TableHeadEnum.jobName}
                        autoFocus={true}
                    />
                </Filters>
            )}

            <PasslyInfiniteScroll
                totalCount={jobCount}
                currentCount={rows.length}
                onFetch={fetchMoreData}
            >
                <div className='user__table notification'>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {headCells.map((headCell: ITableHead, index: number) => (
                                    <TableCell
                                        key={index}
                                    >
                                        {headCell.id === TableHeadEnum.triggers ?
                                            <>
                                                { translation(headCell.label)}
                                            </>
                                            :
                                            <PasslyTableSortLabel
                                                active={orderBy === headCell.id}
                                                direction={order}
                                                onClick={createSortHandler(headCell.id)}
                                            >
                                                {translation(headCell.label)}
                                            </PasslyTableSortLabel>
                                        }
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row: IJobsTableData, index: number) => (
                                <PasslyTableRow key={index}>
                                    <PasslyTableCell>
                                        <Link
                                            component='button'
                                            onClick={() => viewJob(row)}
                                        >
                                            {row.jobName}
                                        </Link>
                                    </PasslyTableCell>
                                    <PasslyTableCell>{row.description}</PasslyTableCell>
                                    <PasslyTableCell>{row.triggers}</PasslyTableCell>
                                    <PasslyTableCell>{row.scheduler}</PasslyTableCell>
                                </PasslyTableRow>
                            ))
                            }
                        </TableBody>
                    </Table>
                </div>
            </PasslyInfiniteScroll>

            {loadingData && <PageLoader />}

            {!loadingData && rows.length === 0 &&
                <div className='table--empty'>{translation('EMPTY_JOBS_LIST')}</div>
            }
        </>
    );

};

export default ScheduledJobsPage;
